import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ApolloError } from 'apollo-client';
import { useStores } from 'src/hooks/use-stores';

import {
  GqlMenuSection,
  useGetCarouselProductsQuery,
  GqlGetCarouselProductsQuery,
  GqlProductsFilterInput,
  MenuSectionType,
  MobileAppFlair,
  MobileAppProductCollectionDisplayType,
} from 'types/graphql';

import { isNotNullish } from 'shared/utils/type-utils';

import { Products } from './product-carousel.types';
import { ProductCarouselV2 } from './product-carousel-v2';

type ProductCarouselControllerProps = {
  menuSection: GqlMenuSection;
  productsFilter: GqlProductsFilterInput;
  asMobileApp?: boolean;
  buttonFlair?: MobileAppFlair;
};

type UseProductCarouselControllerReturn = {
  data?: Products | null | undefined;
  error?: ApolloError;
  loading: boolean;
};

function transformData(
  data: GqlGetCarouselProductsQuery | undefined,
  menuSection: GqlMenuSection
): Products | undefined {
  if (!data?.filteredProducts) {
    return undefined;
  }

  if (menuSection.sectionType === MenuSectionType.custom) {
    const productsOrder = menuSection.products ?? [];
    data.filteredProducts.products.sort((a, b) => {
      if (!a?.id || !b?.id) {
        return 0;
      }
      return productsOrder.indexOf(a.id) - productsOrder.indexOf(b.id);
    });
  }

  return data.filteredProducts.products;
}

function useProductCarouselController({
  menuSection,
  productsFilter,
}: ProductCarouselControllerProps): UseProductCarouselControllerReturn {
  const { apolloClient } = useStores();
  const { id: dispensaryId } = useParams<{ id: string }>();

  const defaultFilter = {
    Status: `Active`,
    sortBy: `popularSortIdx`,
    sortDirection: 1,
    dispensaryId,
  };

  const { data, loading, error } = useGetCarouselProductsQuery({
    client: apolloClient,
    variables: {
      productsFilter: {
        ...defaultFilter,
        ...productsFilter,
      },
    },
  });

  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  return {
    data: transformData(data, menuSection),
    loading,
    error,
  };
}

export function ProductCarouselControllerV2({
  menuSection,
  productsFilter,
  asMobileApp,
  buttonFlair,
}: ProductCarouselControllerProps): JSX.Element {
  const { data, loading } = useProductCarouselController({ menuSection, productsFilter });

  return (
    <ProductCarouselV2
      hasDividerFlair={
        asMobileApp &&
        ((menuSection.mobileAppFlairType != null && menuSection.mobileAppFlairType !== MobileAppFlair.none) ||
          menuSection.mobileAppDisplayType === MobileAppProductCollectionDisplayType.grid)
      }
      label={menuSection.label}
      products={data?.filter(isNotNullish)}
      loading={loading}
      asMobileApp={asMobileApp}
      buttonFlair={buttonFlair}
      mobileAppDisplayType={menuSection.mobileAppDisplayType}
    />
  );
}
